import type { WrittenPage } from '../sections'
import { LINKS } from './links'
import { STACK_GROUPS_EN } from '../stack'
import { COUNTRIES } from '../countries'

/**
 * The home page, from Halil's copy of August 2026.
 *
 * Section order and headline copy are as written. What changed, and why, is in
 * `docs/website/COPY-REVIEW.md` — every cut is listed there so any of it can be
 * put back in one edit.
 */
export const home: WrittenPage = {
  navLabel: 'Home',
  title: '',
  description:
    'Strategy consulting, transformation management and enterprise AI solutions. Digitopia deploys agentic AI so your business gets an outcome, not a pilot.',
  sections: [
    {
      kind: 'hero',
      eyebrow: '#ScaleValue',
      // The line break is deliberate and honoured by the hero's
      // `whitespace-pre-line` h1: the second sentence is the turn of the
      // argument, so it starts its own line instead of wrapping after
      // "Few have". Asked for 2026-08-28.
      headline: 'Every board has approved AI.\nFew have absorbed it.',
      sub: 'Digitopia exists to maximize business outcomes by deploying agentic AI solutions for visionary businesses, with teams of forward deployed engineers and AI agents.',
      micro: 'Discover. Deploy. Outcome.',
      photo: {
        src: '/photo/team-applause.jpg',
        alt: 'A team applauding around a meeting table at the end of a review, a plan worked out on the whiteboard behind them.',
      },
      ctas: [
        { label: 'Book a Call', href: '#contact' },
        { label: 'Check Your AI Readiness', href: LINKS.readinessTool },
      ],
      check:
        'THIS PHOTOGRAPH IS ALSO ON /en/transformation-management. Kagan sent it ' +
        'as the home page hero on 2026-08-26 (the file he attached is the same ' +
        'frame as the team-applause.jpg already in photo-src/, at a smaller ' +
        'export, so the repository copy is the one in use). It currently sits ' +
        'mid-page on Transformation Management, illustrating what a governed ' +
        'review looks like when a decision lands — so a reader going home → ' +
        'Transformation Management meets the same picture twice. Either that ' +
        'second use gets a different photograph or the repeat is accepted ' +
        'deliberately; nothing else on the site repeats an image.\n\n' +
        'The overhead-table shot this replaced is now used only by ' +
        '/en/enterprise-ai-solutions, which resolves the earlier note about the ' +
        'home page and that page sharing a hero. boardroom-impact-platform.jpg — ' +
        'still the only photograph showing the Impact Platform itself — remains ' +
        'unused in photo-src/ and is a one-line edit away if this hero should be ' +
        'a product shot rather than a working session.',
    },
    {
      kind: 'stack',
      groups: STACK_GROUPS_EN,
      check:
        'THREE MARKS STILL MISSING: IFS, DeepSeek and Alibaba. Kagan sourced the ' +
        'other twelve on 2026-08-25 and they render as logos now; these three ' +
        'render as plain-text chips, which reads fine among the others but is a ' +
        'gap rather than a choice. Drop ifs.webp, deepseek.webp and alibaba.webp ' +
        'into apps/website/logo-src/ and run `pnpm --filter @digitopia/website ' +
        'images` — nothing in the content or the component changes. Pull each ' +
        'from that vendor\'s own brand/press kit, never a recreation.\n\n' +
        'Two of the twelve are worth a second look before launch. SALESFORCE is ' +
        'the square cloud icon, which at this row\'s 20px height reads as a small ' +
        'blue blob beside seven wordmarks — their horizontal lockup would sit ' +
        'better. AWS is the official "Powered by AWS" badge, so the "powered by" ' +
        'line above the wordmark is sub-pixel here; that is the file AWS requires ' +
        'for this use, so the question is whether to give it more height rather ' +
        'than whether to swap it.\n\n' +
        'And a factual one, separate from the logos: confirm "integrated" / ' +
        '"connected" / "supported" describe today\'s Agent Core rather than the ' +
        'roadmap. The Enterprise AI Solutions page already lists SAP, Oracle, IFS, ' +
        'Workday and Salesforce in its own description, so those five are covered — ' +
        'the rest of this row is a new claim.'
    },
    {
      kind: 'logos',
      heading: 'Great companies work with Digitopia.',
      sub: '300+ customers. 40+ countries. 20+ industries. 3,000+ measurements.',
      cta: { label: 'See success stories', href: LINKS.successStories },
      items: [
        { name: 'Siemens', src: '/logo/siemens.webp' },
        { name: 'Unilever', src: '/logo/unilever.webp' },
        { name: 'Vodafone', src: '/logo/vodafone.webp' },
        // The 300x300 transparent square, same as every other page that shows
        // this mark. `padded` sizes it by width like LC Waikiki, so the drawn
        // mark matches its neighbours instead of being the smallest in the row.
        // `a101.webp` is a usable tight crop since 2026-08-30 and would work
        // here too — it is greyscale, though, and this band is still mostly
        // colour, so the square stays until the rest of that set arrives.
        { name: 'A101', src: '/logo/a101card.png', padded: true },
        // Four of this band's marks come from the `*card.png` batch rather than
        // the 150x100 marketing crop, because that batch is in brand colour and
        // this crop is greyscale — asked for on 2026-08-30, *"renkli versiyonları
        // varsa o tercih edilsin"*. `padded` is what makes them the right size:
        // both files are canvases with the mark inside, so the height cap would
        // measure the empty canvas and draw the mark half the width of Siemens
        // beside it. Sizing by width makes the drawn ink the shared measure.
        { name: 'Bridgestone', src: '/logo/bridgestonecard.png', padded: true },
        // No colour file exists for these three — only the greyscale 150x100
        // crop, which carries about a third of its height as empty canvas. They
        // are `padded` for the same reason as the colour squares: it is the
        // canvas, not the mark, that the height cap was measuring.
        { name: 'DHL', src: '/logo/dhl.webp', padded: true },
        { name: 'Carlsberg Group', src: '/logo/carlsberggroup.webp', padded: true },
        { name: 'Anadolu Efes', src: '/logo/anadoluefes.webp', padded: true },
        { name: 'Garanti BBVA', src: '/logo/garantibbva.webp' },
        { name: 'ING', src: '/logo/ing.webp' },
        { name: 'Brisa', src: '/logo/brisacard.png', padded: true },
        { name: 'TEMSA', src: '/logo/temsacard.png', padded: true },
        { name: 'Petrol Ofisi', src: '/logo/petrolofisicard.png', padded: true },
        { name: 'Turkish Airlines', src: '/logo/turkishairlines.webp' },
        // The 300x300 transparent square from the `*card.png` batch, asked for
        // here on 2026-08-27 in place of the tight `LCW` crop — same mark, HD
        // and with a real alpha channel. `padded` keeps it at the size the crop
        // rendered at; without it the wall's height cap would draw it half that.
        { name: 'LC Waikiki', src: '/logo/lcwaikikicard.png', padded: true },
        { name: 'Enerjisa', src: '/logo/enerjisa.webp' },
      ],

      check:
        'TWO QUESTIONS ON THIS SECTION — the figures and the logos.\n\n' +
        'FIGURES. These contradict the ones on the live site, which says 15+ ' +
        'industries, 35+ countries and 500+ projects. Anyone who has seen ' +
        'digitopia.co will notice. Reconcile the two sets, and use the same numbers ' +
        'everywhere they appear — they are repeated in the report section, on About ' +
        'and in the meta description.\n\n' +
        'LOGOS — sixteen of sixty. The marketing set has 60 (Marketing → New Website ' +
        'Content → Customer Logo - 150x100); this wall shows the sixteen that read at ' +
        'a glance and balance global with Turkish. Permission is yours to confirm: the ' +
        'folder is named for this website, which is strong evidence the logos were ' +
        'prepared to be published, but a customer logo on a marketing page is their ' +
        'trademark and not ours — ideally get it in writing. (The four wrong-company ' +
        'or unusable files here have all been replaced now: MNG Kargo and Migros on ' +
        '2026-08-30, then a101.webp and asas.webp later the same day, when the A–E ' +
        'half of the greyscale set arrived with a real A101 crop and the ASAŞ ' +
        'diamond.)\n\n' +
        'ONE NEW THING TO LOOK AT. That greyscale set is replacing the wall files ' +
        'company by company, and this band draws the same files — so Bridgestone, ' +
        'DHL, Carlsberg, Anadolu Efes and Brisa are greyscale here now while ' +
        'Siemens, Unilever, Vodafone, Garanti BBVA, ING, TEMSA, Petrol Ofisi, ' +
        'Turkish Airlines and Enerjisa are still in colour. It settles itself when ' +
        'the F–Z half of the set arrives; until then the band is mixed on purpose ' +
        'rather than by accident.'
    },
    {
      kind: 'cards',
      // No heading and no standfirst on purpose (2026-08-28: *"yazmasına gerek
      // yok"*). The three solution names are the heading now, which is what
      // `prominent` sizes them for. The Solutions hub reuses this object and
      // already says the same sentence in its own hero, so it stops saying it
      // twice.
      prominent: true,
      // The DISCOVER / DEPLOY / OUTCOME kickers came off later the same day
      // (*"Discover deploy outcome başlıklarını silelim"*) — the tagline in the
      // hero already names the three steps, and the card titles are the thing
      // the reader is choosing between.
      buttonCtas: true,
      // Left-aligned, not centered (2026-09-01: *"bu explore butonu ortalı
      // olmasın yine eski hali solda dursun"*) — its position before
      // `buttonCtas` turned it into a real button.
      ctaAlignLeft: true,
      // The three solution names carry the same link as their Explore button
      // (2026-08-29 request: the titles should be clickable too). Nothing about
      // the look changes — the anchor inherits the heading's colour and weight
      // and adds no underline; only the cursor gives it away. The Solutions hub
      // reuses this object and already asked for the same thing.
      linkTitles: true,
      items: [
        {
          // Broken over two lines on purpose (2026-08-28) — the other two
          // titles wrap on their own at this size, so left alone this one sat
          // on a single line and started its promise a line higher.
          title: 'Strategy\nConsulting',
          promise: 'Strategize and prioritize your agentic AI journey.',
          points: ['Solve the priorities fight', 'Align your executives', 'Establish a shared vision'],
          cta: { label: 'Explore', href: LINKS.strategy },
        },
        {
          title: 'Transformation Management',
          promise: 'Assure and accelerate your agentic AI journey.',
          points: ['Assure value realization', 'Accelerate execution', 'Govern and sustain change'],
          cta: { label: 'Explore', href: LINKS.transformation },
        },
        {
          title: 'Enterprise AI Solutions',
          promise: 'Deploy your agentic AI solutions at scale.',
          points: ['Establish your agentic stack', 'Realize the benefit', 'Scale confidently'],
          cta: { label: 'Explore', href: LINKS.enterpriseAi },
        },
      ],
    },
    // The "Secure, measurable and successful transformation." split band — drawn
    // heading on the left, a two-slide Rigor/Creativity carousel on the right —
    // was removed from the home page on request (2026-08-28: *"burayı direkt
    // silelim main page den, gerek yok"*). The reader now goes from "Three ways
    // in" straight to the customer quotes. Nothing else linked to it; the
    // lettering photo stays in photo-src if it is ever wanted back.
    {
      // Two cards side by side in the Hall of Fame's own card, pared back —
      // asked for on 2026-08-28: *"bu şekil kutucuklarımız olsun WAIKIKI ve
      // Litehouse olsun… hall of fame deki tasarımın minimal versiyonu…
      // insan görselleri de olsun"*, with digitopia.co's two-up quote boxes as
      // the reference. `variant: 'minimal'` is that card without the arc and
      // without the standing cut-out; the face is the 64px avatar beside the
      // name, which is what the full card already draws below `xl`.
      //
      // Both quotes and both attributions are the ones already on the Hall of
      // Fame cards, so a reader who follows the button meets the same words
      // under the same name rather than a second version of them — LC Waikiki's
      // two paragraphs are the last two of Şerafettin Özer's three, which is
      // exactly the extract that was sent. The two unattributed lines that stood
      // here (both LC Waikiki, no name on digitopia.co either) are gone with
      // them, which also settles the old check on this section: three of its
      // four asks — a name, a role, a second company — are answered by the
      // change itself.
      kind: 'quotes',
      variant: 'minimal',
      heading: 'In their words.',
      items: [
        {
          company: 'LC Waikiki',
          // Measured off the 300x300 file: the wordmark runs almost the full
          // width and about a seventh of the height. Everything else on that
          // canvas is transparent air the card was drawing as a gap.
          // `inkOffsetY: 0.09` (2026-09-02, to stop it reading lower than
          // Litehouse's badge) overshot: it pushed the crop far enough down
          // that the top of every letter was clipped off (2026-09-02, "logo
          // yarım durmamalı, tam şekilde olmalı"). Halved rather than
          // dropped, so the wordmark reads whole while keeping most of the
          // correction toward Litehouse's baseline.
          logo: {
            name: 'LC Waikiki',
            src: '/logo/lcwaikikicard.png',
            ink: 0.15,
            inkWidth: 0.89,
            inkOffsetY: 0.045,
          },
          portrait: { src: '/photo/portrait-serafettin-ozer.png', alt: 'Şerafettin Özer' },
          quote: [
            'We selected Digitopia over major consulting firms and Gartner for their flexible '
            + 'and collaborative approach. We needed a partner who could go beyond a rigid, '
            + 'mechanical process and provide in-depth insights tailored to our needs.',
            'From the very beginning, their openness to feedback and continuous improvement '
            + 'have been key factors in our partnership.',
          ],
          source: 'Şerafettin Özer, Chief Digital and Information Officer',
          cta: { label: 'Read Case Study', href: '/en/customers/lc-waikiki' },
        },
        {
          company: 'Litehouse Foods',
          // The lighthouse badge is about a third of the canvas tall and a bit
          // over half of it wide — `ink: 0.5` was a guess and the difference
          // was 45px of empty canvas under the badge, which is why the two
          // marks did not sit on the same line.
          // `inkScale` because the levelled pair still read as one big mark and
          // one small one: the geometric mean holds their ink *area* equal, and
          // a near-square badge spends that area on two axes where a six-to-one
          // wordmark spends it on one, so the badge lands 133px across against
          // the wordmark's 195. A 1.3 nudge closes most of that without
          // touching the layout — the slot is bottom-aligned, so the badge
          // grows upward and both marks keep the same baseline and the same
          // left edge.
          logo: {
            name: 'Litehouse Foods',
            src: '/logo/litehousecard.png',
            ink: 0.35,
            inkWidth: 0.57,
            inkScale: 1.3,
          },
          portrait: {
            src: '/photo/portrait-ben-bryan.png',
            alt: 'Ben Bryan, smiling, in a grey jacket and open-collar shirt, black and white.',
          },
          quote: [
            'At Litehouse Foods, we partnered with Fisher Management Partners and Digitopia to '
            + 'assess and accelerate our AI journey. The AI Maturity Index gave us a clear '
            + 'baseline, aligned our leadership team, and helped translate our AI vision into a '
            + 'practical roadmap for execution. The AIMI deliverables have helped refine my AI '
            + 'message to our executive team and business leaders. We’re now moving forward '
            + 'with clarity, governance, and real momentum.',
          ],
          source: 'Ben Bryan, Manager, Applications',
          cta: { label: 'Read Case Study', href: '/en/customers/litehouse-foods' },
        },
      ],
      // No button under the pair (2026-08-29: *"en alttaki Read the hall of fame
      // butonuna gerek yok"*). Each card already carries its own, and the Hall
      // of Fame is still one click away from the nav and the footer.
      check:
        'Both quotes now carry a name and a role, taken from the Hall of Fame cards where the ' +
        'same words already appear with the same attribution. Şerafettin Özer’s is the last two ' +
        'paragraphs of a three-paragraph quote — the opening paragraph, about prioritization ' +
        'since 2021, is only on the Hall of Fame card and the story page. Confirm that ' +
        'shortening it here is fine, and that both people are content to be named on the home ' +
        'page rather than only on their own story.',
    },
    {
      // This band used to answer its own question: four unlabelled quadrant
      // profiles — The Unstoppable, Powerful Blind, The Pipe Dream, The Drifter —
      // with the will/skill definitions underneath and no way to act on any of
      // it. Kagan, 2026-08-26: *"sanırım burası bizim tool larımıza
      // yönlendirmeli… tool lara net ve anlaşılır şekilde yönlendirecek şekilde
      // yeniden tasarlayalım."* So it is the same shape as "Three ways in" now —
      // one card per tool, each with its own button, because that is the pattern
      // on this page a reader already knows means "pick one and go".
      //
      // Peers, not a funnel: the order is the reader's likeliest question first,
      // and no card is framed as the warm-up for another (the Tools hub settles
      // that, `pages/tools.ts`). The four profiles are not lost — they are the
      // Transformation Readiness verdict, stated by the tool that computes them
      // rather than recited by a home page that then asks for an email.
      //
      // Durations stay out of the cards and the one time claim in `sub` is the
      // hub's own sentence, verbatim. A per-tool minute count would be its third
      // copy on the site, and three copies of a duration is a duration that can
      // disagree with itself.
      kind: 'cards',
      heading: 'How ready are you, actually?',
      sub: 'Three short assessments, each answering a different question. None of them takes more than six minutes, and each one ends in a verdict you can argue with.',
      // Asked for 2026-09-01, with a screenshot of this band: *"startlar buton
      // olmasın da mavi yazı olsun yanında ok olsun"* — so no `buttonCtas` and
      // no `ctaArrow`, which is the card renderer's default CTA: a bold
      // primary-coloured "Start →" on the left edge of the card.
      // `ruledColumns` keeps the hairlines between the three columns, which
      // `buttonCtas` used to carry and which the request did not ask to lose.
      ruledColumns: true,
      // The three tool names carry the same link as the Start button under
      // them, asked for 2026-08-30 with a screenshot of this band. Nothing
      // about the look changes — the anchor inherits the heading's colour and
      // weight and adds no underline; only the cursor gives it away.
      linkTitles: true,
      // No kickers on these three. "Readiness / Will and skill / Level and gap"
      // named the same thing the title underneath already names, and with a
      // label above them the three titles no longer started on one line
      // (asked for 2026-08-28, with a screenshot of the band). Titles first
      // means the three cards open on the same plane.
      items: [
        {
          title: 'Agentic Readiness Index',
          promise: 'How much of your business you would actually let an agent run, scored on both sides of the question.',
          points: [
            'What the business has authorised, and what the systems can do',
            'Your archetype: ambition-led or capability-led',
            'The one gap that will stop you first',
          ],
          cta: { label: 'Start', href: LINKS.readinessTool },
        },
        {
          title: 'Transformation Readiness',
          promise: 'Whether a transformation you are considering would survive its first difficult quarter.',
          points: [
            'Will and skill as two separate axes, never one average',
            'Which of the four quadrants you are in',
            'Available in English and Turkish',
          ],
          cta: { label: 'Start', href: LINKS.appetiteTool },
        },
        {
          title: 'The Guts Check',
          promise: 'Which of the four levels you are on, and how far your ambition sits from the weakest thing underneath it.',
          points: [
            'Ambition, ownership, execution and proof, scored separately',
            'The ambition–reality gap, which is what predicts a rescope',
            'Three things to do first, specific to your level',
          ],
          cta: { label: 'Start', href: LINKS.gutsCheckTool },
        },
      ],
    },
    {
      // Immediately after the three tools, because this is the thing they produce:
      // the quadrant on a real screen, in a real room, with the room's attention
      // on it.
      //
      // The caption was added on 2026-08-28, when this stretch of the page was
      // read back as three unrelated pictures in a row — a room, a diagram and a
      // chart, none of them saying what it had to do with the one above it. Each
      // of the three now carries one sentence naming its part in the same
      // argument: an assessment ends in a room, the room argues against one
      // model, and the report is what that model measured everywhere else.
      kind: 'photo',
      photo: {
        src: '/photo/office-digital-vitality.jpg',
        alt: 'A Digitopia consultant presenting the Digital Vitality quadrant, endurance against velocity, to three executives in a bright meeting room.',
      },
      caption:
        'Whichever assessment you take, it ends the same way: a score on the screen and the people who have to act on it arguing with it.',
    },
    {
      // The DAIMI wheel, immediately before the band that offers the report
      // built on it — asked for on 2026-08-28 (*"homepage burada kullanmamız
      // gereken DAIMI görseli"*), with the Turkish export attached. It is a
      // `figure` and not a `photo` because the wheel is ~2.4:1 with its
      // dimension names printed down both edges, and `photo`'s 16:9 crop would
      // cut off exactly those labels; a figure also sits on white, which these
      // transparent navy-lettered exports need. The Turkish export goes on the
      // Turkish home page and this one shows the English wheel, so neither
      // reader meets the other language's drawing.
      kind: 'figure',
      photo: {
        src: '/photo/daimi-framework-wheel-labelled.png',
        alt: 'The DAIMI wheel, showing Customer, Operations, Innovation, People, Governance and Technology around the Digital & AI Maturity Index, with the sub-dimensions measured under each.',
      },
      caption:
        'All three assessments score against one model: the six DAIMI dimensions, and the sub-dimensions measured under each.',
      // Clickable to DAIMI's own page, asked for 2026-08-31 — no visible
      // change, same destination the identical wheel already links to
      // beside its "Explore DAIMI" button on the Strategy Consulting page.
      href: LINKS.daimi,
    },
    {
      kind: 'cta',
      heading: 'Digital & AI Maturity Report 2026',
      sub: 'Where your sector stands on those same six dimensions, and what separates the top quartile from everyone else. Built from completed assessments, not a survey panel. 3,000+ measurements, 40+ countries, 20+ industries.',
      tone: 'band',
      // The index chart the report is built on, in the empty right half of the
      // band. Same file the DAIMI landing page shows, so the number a reader
      // meets here is the number they meet there.
      photo: {
        src: '/photo/daimi-maturity-index-gap.webp',
        alt: 'Maturity Index by year: DMI rising from 2.70 in 2020 to 3.06 in 2025, DAIMI at 2.93 in 2025, AIMI at 2.30 in 2024 and 2.38 in 2025',
      },
      // Clickable to the report this chart is drawn from, asked for
      // 2026-08-31 — no visible change.
      photoHref: LINKS.daimiReport,
      // The heading too, asked for 2026-09-08 — same destination as the chart
      // and the button beside it, and no visible change.
      headingHref: LINKS.daimiReport,
      ctas: [
        // Straight to the report's own page rather than the Reports index,
        // asked for 2026-09-08 — the button names one report, so a list was a
        // step in the way. Same destination as the chart beside it.
        { label: 'Download the 2026 report', href: LINKS.daimiReport },
        { label: 'Check your AI readiness', href: LINKS.readinessTool },
      ],
    },
    {
      kind: 'form',
      // The statement beside the form, not a description of it. Asked for on
      // 2026-08-26 with the old digitopia.co contact page as the reference:
      // "Transform Better." and its explanatory paragraph out, the three words
      // of the method in their place, with the brand line under them. So this
      // heading is deliberately not a sentence, and `sub` is deliberately not
      // prose — see the `form` case in ContentPage.tsx for how they are set.
      heading: 'DISCOVER. DEPLOY. OUTCOME.',
      // Asked for 2026-09-08: this line must not wrap to a second line for
      // "OUTCOME." alone.
      headingNoWrap: true,
      sub: '#SCALEVALUE',
      fields: [
        { name: 'name', label: 'Full name', type: 'text', required: true },
        { name: 'email', label: 'Work email', type: 'email', required: true },
        { name: 'company', label: 'Company', type: 'text', required: true },
        {
          name: 'country',
          label: 'Country',
          type: 'select',
          placeholder: 'Select a country',
          options: COUNTRIES,
        },
        { name: 'goal', label: 'Message', type: 'textarea' },
      ],
      consent: 'I agree to Digitopia contacting me about my enquiry.',
      submit: 'Send',
      confirmation: 'Thank you. We read every message. Expect a reply from a practice lead within two business days.',
      check:
        'Confirm the two business day promise before publishing. It is the easiest ' +
        'commitment on the site to break, and the one a prospect will notice. If you ' +
        'cannot hold it, change it or drop the number. ' +
        // Asked on 2026-08-27, answered no, and answered yes on 2026-08-30 — the
        // forms post now, so the half of this note about a disabled Send button is
        // gone. What survives is the pair below, and it survives because both are
        // decisions: whether an enquiry gets an acknowledgement at all, and whether
        // "which country" may route a lead when the only thing the form records is
        // which page it was filled on.
        'Both contact forms submit now (2026-08-30) — they post to '
        + '`website-form-lead`, open a lead and a task on the page\'s own counterpart '
        + '(Website → Forms), and a gated download is emailed to the address given. '
        + 'No HubSpot form id is needed for any of that. TWO THINGS FROM THE OLD '
        + 'WORKFLOW ARE STILL MISSING, and both are decisions rather than code. '
        + 'First, a plain enquiry gets no acknowledgement: digitopia.co replies from '
        + 'info@m.digitopia.co and this site sends nothing back unless the visitor '
        + 'asked for a document. Say whether an acknowledgement should go out, and '
        + 'what it should say. Second, routing is per PAGE, not per country: '
        + '"halil + feyza for a Türkiye enquiry, halil + fatih.eryigit for anywhere '
        + 'else" cannot be expressed, because the form records which page it was '
        + 'filled on and not where the person is. Either name one counterpart per '
        + 'page and accept that, or say the country field should drive it and it '
        + 'becomes a second routing rule.',
    },
  ],
}
